// src/pages/workflow/HistorialWorkflow.jsx - CÓDIGO COMPLETO CON @mui/lab
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Paper,
  Container,
  Tooltip,
  IconButton,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';

// ✅ IMPORTACIÓN CORRECTA desde @mui/lab
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';

import {
  History as HistoryIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import { 
  obtenerEstadoInscripcion, 
  obtenerDocumentosRequeridos,
  obtenerResumenEstados
} from '../../redux/slices/workflowSlice';
import InformeDocumentos from '../../components/workflow/InformeDocumentos';
import useStyles from '../../theme/useStyles';

const HistorialWorkflow = () => {
  const { entidadId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  
  const { 
    estadoWorkflow, 
    documentosRequeridos, 
    resumenEstados,
    loading 
  } = useSelector((state) => state.workflow);

  const [historial, setHistorial] = useState(null);
  const [entidadInfo, setEntidadInfo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [modalInforme, setModalInforme] = useState(false);

  useEffect(() => {
    cargarDatosCompletos();
  }, [entidadId]);

  const cargarDatosCompletos = async () => {
    setCargando(true);
    setError(null);
    
    try {
      console.log('🔄 Cargando historial completo para entidad:', entidadId);

      // 1. Obtener estado del workflow
      const resultadoEstado = await dispatch(obtenerEstadoInscripcion(entidadId));
      
      if (resultadoEstado.payload?.esExitoso && resultadoEstado.payload?.estado) {
        const estadoData = resultadoEstado.payload.estado;
        
        // 2. Obtener información de la entidad
        setEntidadInfo({
          id: entidadId,
          nombre: estadoData.entidad?.nombre || 'Entidad Desconocida',
          nit: estadoData.entidad?.nit || 'N/A',
          workflowNegocioId: estadoData.workflowNegocioId
        });

        // 3. Obtener documentos y resumen si existe workflow
        if (estadoData.workflowNegocioId) {
          await Promise.all([
            dispatch(obtenerDocumentosRequeridos(estadoData.workflowNegocioId)),
            dispatch(obtenerResumenEstados(estadoData.workflowNegocioId))
          ]);

          // 4. Construir historial con datos reales
          construirHistorial(estadoData);
        } else {
          setHistorial({
            entidadId: entidadId,
            nombreEntidad: estadoData.entidad?.nombre || 'Entidad',
            estadoActual: 'Sin proceso iniciado',
            fechaInicio: null,
            pasos: [],
            documentos: []
          });
        }
      } else {
        throw new Error('No se pudo obtener el estado del workflow');
      }
    } catch (err) {
      console.error('Error cargando historial:', err);
      setError('Error al cargar el historial del proceso');
    } finally {
      setCargando(false);
    }
  };

  const construirHistorial = (estadoData) => {
    const pasos = estadoData.pasos || [];
    const estadoActual = getEstadoTexto(estadoData.estadoWorkflow);
    
    // Construir historial con pasos reales
    const historialData = {
      entidadId: entidadId,
      nombreEntidad: estadoData.entidad?.nombre || 'Entidad',
      workflowNegocioId: estadoData.workflowNegocioId,
      estadoActual: estadoActual,
      fechaInicio: estadoData.fechaInicio,
      fechaUltimaActividad: estadoData.fechaUltimaActividad,
      pasos: pasos.map(paso => ({
        id: paso.id,
        nombrePaso: paso.nombrePaso,
        codigoPaso: paso.codigoPaso,
        ordenPaso: paso.ordenPaso,
        estadoNegocioId: paso.estadoNegocioId,
        nombreEstado: getEstadoTexto(paso.estadoNegocioId),
        colorEstado: getColorEstado(paso.estadoNegocioId),
        fechaInicio: paso.fechaInicio,
        fechaCompletado: paso.fechaCompletado,
        fechaUltimaActividad: paso.fechaUltimaActividad,
        usuarioAsignado: paso.usuarioAsignado,
        comentarios: paso.comentarios || generarComentarioAutomatico(paso)
      })),
      documentos: [] // Se llenará con los documentos del Redux
    };

    setHistorial(historialData);
  };

  const generarComentarioAutomatico = (paso) => {
    switch (paso.codigoPaso) {
      case 'INICIO':
        return 'Proceso de inscripción iniciado correctamente';
      case 'REGISTRO':
        return 'Validación de entidad, usuarios y contactos';
      case 'CARGA_DOCS':
        return 'Carga y radicación de documentos requeridos';
      case 'REVISION':
        return 'Revisión administrativa de documentos';
      case 'FINALIZACION':
        return 'Proceso de inscripción finalizado';
      default:
        return `Paso ${paso.nombrePaso} en proceso`;
    }
  };

  const getEstadoTexto = (estadoId) => {
    const estados = {
      1: 'Iniciado',
      2: 'Registro Incompleto',
      3: 'Registro Completo',
      4: 'En Revisión',
      5: 'Completado',
      6: 'Requiere Atención',
      7: 'Documentos Pendientes',
      8: 'Documentos Cargados'
    };
    return estados[estadoId] || 'Estado Desconocido';
  };

  const getColorEstado = (estadoId) => {
    const colores = {
      1: '#3498db', // Azul
      2: '#f39c12', // Naranja
      3: '#27ae60', // Verde
      4: '#f39c12', // Naranja
      5: '#27ae60', // Verde
      6: '#e74c3c', // Rojo
      7: '#f39c12', // Naranja
      8: '#27ae60'  // Verde
    };
    return colores[estadoId] || '#95a5a6';
  };

  const getDocumentStatusChip = (documento) => {
    const estados = {
      1: { label: 'Pendiente', color: 'default' },
      2: { label: 'Radicado', color: 'primary' },
      3: { label: 'En Revisión', color: 'warning' },
      4: { label: 'Aprobado', color: 'success' },
      5: { label: 'Rechazado', color: 'error' },
      6: { label: 'Requiere Corrección', color: 'warning' },
      7: { label: 'Pendiente Recarga', color: 'error' },
      8: { label: 'Subido', color: 'info' }
    };
    
    const estado = estados[documento.estadoDocumentoId] || { label: 'Desconocido', color: 'default' };
    return <Chip label={estado.label} color={estado.color} size="small" />;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calcularDuracion = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return 'N/A';
    
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diffMs = fin - inicio;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} día(s) ${diffHours} hora(s)`;
    } else if (diffHours > 0) {
      return `${diffHours} hora(s)`;
    } else {
      return 'Menos de 1 hora';
    }
  };

  if (cargando) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Cargando historial del proceso...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error al cargar historial</AlertTitle>
          {error}
          <Box sx={{ mt: 2 }}>
            <Button onClick={cargarDatosCompletos} startIcon={<RefreshIcon />}>
              Reintentar
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 3 }}>
      <Card
        sx={{
          maxWidth: 1200,
          margin: 'auto',
          mt: 4,
          p: 2
        }}
      >
        <CardHeader 
          classes={{ title: classes.cardTitle }}
          avatar={<HistoryIcon style={{ fontSize: "large", color: "#494949" }} />}
          title="Historial del Proceso de Inscripción" 
          subheader={`${historial?.nombreEntidad} | Estado: ${historial?.estadoActual}`}
          action={
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <Tooltip title="Generar Informe" arrow>
                  <IconButton
                    onClick={() => setModalInforme(true)}
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!documentosRequeridos || documentosRequeridos.length === 0}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Actualizar" arrow>
                  <IconButton
                    onClick={cargarDatosCompletos}
                    variant="contained"
                    color="primary"
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Volver al Dashboard" arrow>
                  <IconButton
                    onClick={() => navigate(`/inscripcion/${entidadId}`)}
                    variant="contained"
                    color="primary"
                    size="small"
                  >
                    <DashboardIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Inicio" arrow>
                  <IconButton
                    onClick={() => navigate('/')}
                    variant="contained"
                    color="secondary"
                    size="small"
                  >
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          }
        />
        
        <CardContent>
          {/* Información del proceso */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Información General
            </Typography>
            
            <Paper elevation={1} sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                      Fecha de Inicio:
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatFecha(historial?.fechaInicio)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTimeIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                      Última Actividad:
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatFecha(historial?.fechaUltimaActividad)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ScheduleIcon sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                      Duración Total:
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {calcularDuracion(historial?.fechaInicio, historial?.fechaUltimaActividad)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Estadísticas rápidas */}
              {resumenEstados && (
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'grey.300' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 'medium' }}>
                    Resumen de Documentos:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item>
                      <Chip 
                        label={`Total: ${resumenEstados.totalDocumentos || 0}`} 
                        variant="outlined" 
                        size="small" 
                      />
                    </Grid>
                    <Grid item>
                      <Chip 
                        label={`Aprobados: ${resumenEstados.documentosAprobados || 0}`} 
                        color="success" 
                        size="small" 
                      />
                    </Grid>
                    <Grid item>
                      <Chip 
                        label={`En Revisión: ${resumenEstados.documentosEnRevision || 0}`} 
                        color="warning" 
                        size="small" 
                      />
                    </Grid>
                    <Grid item>
                      <Chip 
                        label={`Pendientes: ${resumenEstados.documentosPendientes || 0}`} 
                        color="default" 
                        size="small" 
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Box>

          {/* ✅ TIMELINE con @mui/lab */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Línea de Tiempo del Proceso
            </Typography>
            
            <Paper elevation={1} sx={{ p: 3 }}>
              <Timeline position="right">
                {historial?.pasos?.map((paso, index) => (
                  <TimelineItem key={paso.id}>
                    <TimelineOppositeContent
                      sx={{ m: 'auto 0' }}
                      align="right"
                      variant="body2"
                      color="text.secondary"
                    >
                      {formatFecha(paso.fechaInicio)}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot 
                        sx={{ 
                          bgcolor: paso.colorEstado,
                          border: '2px solid white',
                          boxShadow: 2
                        }}
                      >
                        {paso.fechaCompletado ? 
                          <CheckCircleIcon sx={{ color: 'white', fontSize: 16 }} /> : 
                          <ScheduleIcon sx={{ color: 'white', fontSize: 16 }} />
                        }
                      </TimelineDot>
                      {index < (historial?.pasos?.length || 0) - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: 2 }}>
                      <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                        {paso.nombrePaso}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Estado: {paso.nombreEstado}
                      </Typography>
                      {paso.fechaCompletado && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Completado: {formatFecha(paso.fechaCompletado)}
                        </Typography>
                      )}
                      
                      <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Duración:</strong> {calcularDuracion(paso.fechaInicio, paso.fechaCompletado || new Date())}
                        </Typography>
                        {paso.usuarioAsignado && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Usuario Asignado:</strong> {paso.usuarioAsignado}
                          </Typography>
                        )}
                        {paso.comentarios && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium', mb: 1 }}>
                              Comentarios:
                            </Typography>
                            <Typography variant="body2">
                              {paso.comentarios}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Paper>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Estado de documentos */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Estado de Documentos
            </Typography>
            
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Subido por</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Subida</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Revisado por</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Revisión</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentosRequeridos?.map((documento) => (
                    <TableRow key={documento.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {documento.nombreTipoDocumento}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getDocumentStatusChip(documento)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {documento.usuarioSubida ? (
                            <>
                              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                                <PersonIcon sx={{ fontSize: 14 }} />
                              </Avatar>
                              <Typography variant="body2">
                                {documento.usuarioSubida}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFecha(documento.fechaSubida)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {documento.usuarioRevisor ? (
                            <>
                              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'secondary.main' }}>
                                <PersonIcon sx={{ fontSize: 14 }} />
                              </Avatar>
                              <Typography variant="body2">
                                {documento.usuarioRevisor}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFecha(documento.fechaRevision)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 200, 
                            wordWrap: 'break-word',
                            fontStyle: documento.comentarioRevision ? 'normal' : 'italic',
                            color: documento.comentarioRevision ? 'text.primary' : 'text.secondary'
                          }}
                        >
                          {documento.comentarioRevision || 'Sin comentarios'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!documentosRequeridos || documentosRequeridos.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No hay documentos registrados para este proceso
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Estado final del proceso */}
          {resumenEstados?.estadoWorkflow === 5 && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <AlertTitle>Proceso Completado Exitosamente</AlertTitle>
              <Typography variant="body2">
                El proceso de inscripción ha sido finalizado. Todos los documentos han sido revisados y aprobados.
                Puede generar el informe final para sus registros.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={() => setModalInforme(true)}
                  startIcon={<DownloadIcon />}
                >
                  Generar Informe Final
                </Button>
              </Box>
            </Alert>
          )}
        </CardContent>

        {/* Modal del informe */}
        <InformeDocumentos
          open={modalInforme}
          onClose={() => setModalInforme(false)}
          entidadInfo={entidadInfo}
          documentos={documentosRequeridos}
          resumenEstados={resumenEstados}
          estadoWorkflow={resumenEstados?.estadoWorkflow}
        />
      </Card>
    </Container>
  );
};

export default HistorialWorkflow;